import { Box, IconButton, Tooltip } from '@material-ui/core';
import {
  Checkbox,
  EditIconTwoTone,
  LoadingSpinnerIconTwoTone,
  StyledTooltip,
  Typography,
  useConfirmationModal,
} from '@room-match/shared-ui-components';
import React, { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { ICategoryGroup, ISkill, ISkillCategory } from 'shared/interfaces/ISkills';
import { talentService } from 'shared/services/talentService';
import SKILLS_DATA from '../../../../data/Skills.json';
import SelectPopover from './Popovers/SelectPopover';
import {
  ActiveBorder,
  CheckboxContainer,
  ControlCont,
  GroupContainer,
  Heading,
  InitialsBox,
  MapCont,
  ProficiencyCont,
  SkillContainer,
  useStyles,
} from './SkillComponents.style';
import { CustomTypography } from 'shared/elements/CustomTypography';

interface Props {
  groupsData: ISkillCategory['groups'];
  category: ISkillCategory['category'];
  skipPopup?: Boolean;
  hideMediaViewActions?: Boolean;
}

const { updateTalent, getSkills } = talentService();

const SkillGroupComponent: React.FC<Props> = ({ groupsData, category, skipPopup, hideMediaViewActions }) => {
  const queryClient = useQueryClient();
  const classes = useStyles();
  const [groups, setGroups] = useState<ISkillCategory['groups']>(groupsData);
  const { mutateAsync: updateSkill, isLoading } = updateTalent();
  const { data: skillData, refetch } = getSkills();
  const [showGroup, setshowGroup] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [selectedSkill, setselectedSkill] = useState<ISkill | null>(null);
  const [saving, setSaving] = useState(false);
  const [selectedGroupName, setSelectedGroupName] = useState<ICategoryGroup['name']>('');
  const [skills, setskills] = useState<ISkillCategory[]>(SKILLS_DATA as ISkillCategory[]);
  const [currChange, setCurrChange] = useState<string>('');

  const {
    ConfirmationModal: ConfirmationModal,
    handleOpen: handleOpenConfirm,
    handleSetMessage: handleConfirmSetMessage,
    isOpen: isConfirmModalOpen,
  } = useConfirmationModal({
    type: 'warning',
  });

  useEffect(() => {
    if (!skillData?.data.attributes.skills?.length || typeof skillData?.data.attributes.skills === 'string') {
      return;
    }

    setskills(skillData?.data.attributes.skills || []);
  }, [skillData]);

  const updateSkills = async (mSkill: ISkill, gName: ICategoryGroup['name']) => {
    setSaving(true);

    const mData: ISkillCategory[] =
      skills.map((m) => {
        if (m.category === category) {
          const mGroup =
            m.groups?.map((g) => {
              if (g.name === gName) {
                const mSkills = g.skills.map((s) => {
                  if (s.key === mSkill.key) {
                    return mSkill;
                  }
                  return s;
                });

                return {
                  ...g,
                  skills: mSkills,
                };
              }
              return g;
            }) || [];
          setGroups(mGroup);
          return {
            ...m,
            groups: mGroup,
          };
        }
        return m;
      }) || [];

    await updateSkill(
      {
        skills: mData,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['talents/skills']);
          setskills(mData);
          setAnchorEl(null);
          setCurrChange('');
        },
      },
    );

    setTimeout(() => setSaving(false), 500);
  };

  const removeSkill = () => {
    if (!selectedSkill) return;
    updateSkills(
      { ...selectedSkill, is_selected: false, media: [], proficiency: { key: 'Novice', value: 'N' } },
      selectedGroupName,
    );
  };

  const handleClick = async (event: any, skill: ISkill, gName: ICategoryGroup['name'], edit: boolean) => {
    setselectedSkill(skill);
    setSelectedGroupName(gName);
    setCurrChange(skill.key);

    if (skill.is_selected && !edit) {
      handleOpenConfirm();
      handleConfirmSetMessage('Remove Skill', `Are you sure you want to remove \n ${skill.key} to your skill?`, 'Yes');
      return;
    }

    setAnchorEl(event.currentTarget);

    if (skipPopup) {
      if (!skill.is_selected) {
        await updateSkills(
          {
            ...skill,
            is_selected: true,
            media: [],
            proficiency: { key: 'Novice', value: 'N' },
          },
          gName,
        );
      }
    }
  };

  const popoverSave = (skill: ISkill) => {
    setSaving(true);
    updateSkills(skill, selectedGroupName);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <MapCont>
      {groups?.map((group) => (
        <GroupContainer key={group.name}>
          <Heading>{group.name}</Heading>
          <SkillContainer className={`${showGroup ? 'show-group' : ''}`}>
            {group.skills.map((skill) => {
              return (
                <ControlCont isSelected={skill.is_selected}>
                  {skill.is_selected && <ActiveBorder />}
                  <Box style={{ zIndex: 10 }}>
                    <CheckboxContainer style={{ opacity: saving && currChange !== skill.key ? 0.3 : 1 }}>
                      <Checkbox
                        disabled={saving}
                        color="primary"
                        checked={skill.is_selected}
                        onChange={(e) => {
                          if (saving) {
                            e.preventDefault();
                            return;
                          }
                          handleClick(e, skill, group.name, false);
                        }}
                        value={skill.is_selected}
                        label={skill.key}
                        key={skill.key}
                      />
                      {saving && currChange === skill.key && <LoadingSpinnerIconTwoTone />}
                    </CheckboxContainer>
                    {skill.is_selected && !hideMediaViewActions ? (
                      <ProficiencyCont>
                        <Tooltip title={skill.proficiency?.key || 'Novice'}>
                          <InitialsBox>
                            <Typography fontSize={11}>{skill.proficiency?.value || 'N'}</Typography>
                          </InitialsBox>
                        </Tooltip>
                        <CustomTypography style={{ marginRight: 7 }} fontSize={14}>
                          {skill.media?.length || 'No'} media attached
                        </CustomTypography>
                        <Tooltip placement="right" title={'Edit'}>
                          <IconButton
                            disableFocusRipple
                            size="small"
                            style={{ marginTop: -5 }}
                            onClick={(e) => {
                              handleClick(e, skill, group.name, true);
                            }}
                          >
                            <EditIconTwoTone style={{ width: 16, height: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </ProficiencyCont>
                    ) : null}
                  </Box>
                </ControlCont>
              );
            })}
          </SkillContainer>
          {selectedSkill && !skipPopup ? (
            <SelectPopover
              open={open}
              anchorEl={anchorEl}
              skill={selectedSkill}
              close={handleClose}
              onSave={popoverSave}
            />
          ) : null}
        </GroupContainer>
      ))}
      {isConfirmModalOpen && <ConfirmationModal onClose={() => {}} onConfirm={() => removeSkill()} />}
    </MapCont>
  );
};

export default SkillGroupComponent;
